import { type MutationFunctionOptions, type MutationHookOptions, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import type {
  CreateBlankProductionRunTemplateInput,
  createProductionRun as CreateProductionRunData,
  createProductionRunFromTemplate as CreateProductionRunFromTemplateData,
  createProductionRunFromTemplateVariables as CreateProductionRunFromTemplateVariables,
  CreateProductionRunInput,
  createProductionRunTemplate as CreateProductionRunTemplateData,
  createProductionRunTemplateVariables as CreateProductionRunTemplateVariables,
  createProductionRunVariables as CreateProductionRunVariables,
} from 'types/graphql';

/** Which door creates. Each keeps its own operation name (telemetry); root and selection are identical. */
export type CreateDoorOrigin = 'listPage' | 'createNewMenu';

/** `/production-runs` → New → Production Run. */
const CREATE_PRODUCTION_RUN_MUTATION = gql`
  mutation createProductionRun($input: CreateProductionRunInput!) {
    createProductionRun(input: $input) {
      id
      slug
    }
  }
`;

/** Header "Create new…" → Production Run. */
const CREATE_PRODUCTION_RUN_FROM_CREATE_NEW_MUTATION = gql`
  mutation createProductionRunFromCreateNew($input: CreateProductionRunInput!) {
    createProductionRun(input: $input) {
      id
      slug
    }
  }
`;

const CREATE_PRODUCTION_RUN_TEMPLATE_MUTATION = gql`
  mutation createProductionRunTemplate($input: CreateBlankProductionRunTemplateInput!) {
    createBlankProductionRunTemplate(input: $input) {
      id
      slug
    }
  }
`;

const CREATE_PRODUCTION_RUN_FROM_TEMPLATE_MUTATION = gql`
  mutation createProductionRunFromTemplate($templateId: Int!) {
    createProductionRunFromTemplate(templateId: $templateId) {
      id
      slug
    }
  }
`;

type CreateProductionRunOptions = Pick<
  MutationHookOptions<CreateProductionRunData, CreateProductionRunVariables>,
  'onError'
> & { origin?: CreateDoorOrigin };

export function useCreateProductionRun({ origin = 'listPage', ...options }: CreateProductionRunOptions = {}) {
  const [mutate, { loading, error }] = useMutation<CreateProductionRunData, CreateProductionRunVariables>(
    origin === 'createNewMenu' ? CREATE_PRODUCTION_RUN_FROM_CREATE_NEW_MUTATION : CREATE_PRODUCTION_RUN_MUTATION,
    options
  );
  const create = useCallback(
    (
      input: CreateProductionRunInput,
      callOptions?: Pick<MutationFunctionOptions<CreateProductionRunData, CreateProductionRunVariables>, 'onCompleted'>
    ) => mutate({ variables: { input }, ...callOptions }),
    [mutate]
  );
  return { create, loading, error };
}

export function useCreateProductionRunTemplate() {
  const [mutate, { loading, error }] = useMutation<
    CreateProductionRunTemplateData,
    CreateProductionRunTemplateVariables
  >(CREATE_PRODUCTION_RUN_TEMPLATE_MUTATION);
  const create = useCallback(
    (input: CreateBlankProductionRunTemplateInput) => mutate({ variables: { input } }),
    [mutate]
  );
  return { create, loading, error };
}

export function useCreateProductionRunFromTemplate() {
  const [mutate, { loading, error }] = useMutation<
    CreateProductionRunFromTemplateData,
    CreateProductionRunFromTemplateVariables
  >(CREATE_PRODUCTION_RUN_FROM_TEMPLATE_MUTATION);
  const create = useCallback((templateId: number) => mutate({ variables: { templateId } }), [mutate]);
  return { create, loading, error };
}
